import type { Course, CoursePrice } from "../api";
import type { Locale } from "@/lib/i18n";

/**
 * What a course card shows as its price (site review 2026-09-04, item 6).
 *
 * A course that sells through pricing plans never shows a crossed-out price:
 * two or more plans read "From X", one plan reads that plan's price. Only a
 * course with no plans keeps the legacy list/sale pair.
 */
export type CardPrice =
  | { kind: "free" }
  | { kind: "from"; amount: number }
  | { kind: "single"; amount: number }
  | { kind: "sale"; amount: number; listAmount: number }
  | { kind: "list"; amount: number };

export type CardPriceInput = {
  price: CoursePrice;
  planCount?: number | undefined;
  fromPrice?: number | null | undefined;
};

export function resolveCardPrice(course: CardPriceInput): CardPrice {
  const planCount = course.planCount ?? 0;
  const fromPrice = course.fromPrice ?? null;
  if (planCount > 0 && fromPrice !== null) {
    if (fromPrice <= 0) return { kind: "free" };
    return planCount >= 2
      ? { kind: "from", amount: fromPrice }
      : { kind: "single", amount: fromPrice };
  }
  const { list, sale, isFree } = course.price;
  if (isFree) return { kind: "free" };
  if (sale !== undefined && sale < list) return { kind: "sale", amount: sale, listAmount: list };
  return { kind: "list", amount: list };
}

export function formatBdt(amount: number): string {
  return `৳${amount.toLocaleString("en-IN")}`;
}

/** "From ৳6,999" / "শুরু ৳6,999 থেকে": one source for every card. */
export function fromPriceLabel(amount: number, locale: Locale): string {
  return locale === "bn" ? `শুরু ${formatBdt(amount)} থেকে` : `From ${formatBdt(amount)}`;
}

export function freeLabel(locale: Locale): string {
  return locale === "bn" ? "ফ্রি" : "Free";
}

export type { Course as CardPriceCourse };
